import React, { useMemo, useState } from "react";
import { Button } from "../Button";
import { CompetitionStage, CompetitionStageWritePayload, KnockoutLevel, MatchFormat, SeedingMethod, StageType, TIE_BREAKER_OPTIONS } from "../../../types";
import { moveItem } from "../../../utils/array";

interface CompetitionCreatePanelProps {
  canManage: boolean;
  eventId: number;
  participantsCount: number;
  existingStages: CompetitionStage[];
  creating: boolean;
  onCreateStage: (payload: CompetitionStageWritePayload) => Promise<void>;
}

const KNOCKOUT_OPTIONS: { value: KnockoutLevel; label: string }[] = [
  { value: "round_of_16", label: "Ottavi" },
  { value: "quarter_finals", label: "Quarti" },
  { value: "semi_finals", label: "Semifinali" },
  { value: "final", label: "Finale" },
];

export const CompetitionCreatePanel: React.FC<CompetitionCreatePanelProps> = ({
  canManage,
  eventId,
  participantsCount,
  existingStages,
  creating,
  onCreateStage,
}) => {
  const nextOrder = useMemo(() => {
    if (existingStages.length === 0) return 1;
    return Math.max(...existingStages.map((s) => s.order)) + 1;
  }, [existingStages]);

  const [name, setName] = useState("");
  const [stageType, setStageType] = useState<StageType>("groups");
  const [order, setOrder] = useState<number>(nextOrder);
  const [seedingMethod, setSeedingMethod] = useState<SeedingMethod>("random");
  const [matchFormat, setMatchFormat] = useState<MatchFormat>("single");
  const [numGroups, setNumGroups] = useState<number>(2);
  const [qualifiers, setQualifiers] = useState<number>(2);
  const [wildcards, setWildcards] = useState<number>(0);
  const [erroreWildcards, setErroreWildcards] = useState<string>("");
  const [knockoutLevel, setKnockoutLevel] = useState<KnockoutLevel>("quarter_finals");
  const [rankingStartPosition, setRankingStartPosition] = useState<number>(0);
  const [tieBreakers, setTieBreakers] = useState<string>(TIE_BREAKER_OPTIONS.map((option) => option.value).join(","));

  React.useEffect(() => {
    setOrder(nextOrder);
  }, [nextOrder]);

  const handleCreate = async () => {
    if (!canManage || creating) return;
    if (participantsCount < 2) {
      alert("Servono almeno 2 partecipanti.");
      return;
    }
    if (order < 1) {
      alert("L'ordine deve essere >= 1.");
      return;
    }
    if (stageType === "groups" && numGroups < 1) {
      alert("Numero gironi non valido.");
      return;
    }
    if (stageType === "knockout" && rankingStartPosition < 1) {
      alert("Posizione iniziale ranking non valida.");
      return;
    }

    const payload: CompetitionStageWritePayload = {
      event: eventId,
      name: name.trim() || (stageType === "groups" ? "Gironi" : "Eliminatorie"),
      stage_type: stageType,
      order,
      seeding_method: seedingMethod,
      ranking_start_position: stageType === "knockout" ? rankingStartPosition : null,
      knockout_stage_type: stageType === "knockout" ? knockoutLevel : null,
      tie_breakers: tieBreakers
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      match_format: stageType === "groups" ? matchFormat : null,
      num_groups: stageType === "groups" ? numGroups : null,
      qualifiers_per_group: stageType === "groups" ? qualifiers : null,
      wildcards: stageType === "groups" ? wildcards : null,
    };

    await onCreateStage(payload);
    setName("");
  };

  const handleTieBreakersMoveUp = (index: number) => {
    setTieBreakers(moveItem(tieBreakers.split(","), index, index - 1).join(","));
  };

  const handleTieBreakersMoveDown = (index: number) => {
    setTieBreakers(moveItem(tieBreakers.split(","), index, index + 1).join(","));
  };

  const handleWildcards = (numGroups: number, qualifiers: number) => {
    let wildcardsValue = -1;
    let msg_errore = ""
    if (numGroups > 0 && qualifiers > 0) {
      let totalDirectQualifiers = numGroups * qualifiers;
      if (participantsCount >= totalDirectQualifiers) {
        const knockoutSteps = [2, 4, 8, 16];
        for (let i = 0; i < knockoutSteps.length; i++) {
          const step = knockoutSteps[i];
          if (step >= totalDirectQualifiers) {
            wildcardsValue = step - totalDirectQualifiers;
            break;
          }
        }
      }
      if (wildcardsValue < 0) {
        msg_errore = (
          "Impossibile calcolare il numero di wildcards! " 
          + "Qualificati diretti: " + totalDirectQualifiers + "; "
          + "Partecipanti: " + participantsCount + ";"
        );
      }
    }
    if (msg_errore.length === 0 && wildcardsValue >= 0) {
      setWildcards(wildcardsValue);
      setErroreWildcards("");
    }
    else {
      setErroreWildcards(msg_errore);
    }
  };

  return (
    <div className="bg-white border border-gray-300 rounded shadow-sm p-6 mt-6">
      <h2 className="text-lg font-semibold mb-4">Crea Fase Competizione</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">
          Nome fase
          <input
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Es. Gironi A-B"
            disabled={!canManage}
          />
        </label>

        <label className="text-sm">
          Numero fase
          <input
            type="number"
            min={1}
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            disabled={!canManage}
          />
        </label>

        <label className="text-sm">
          Match format
          <select
            className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
            value={matchFormat}
            onChange={(e) => setMatchFormat(e.target.value as MatchFormat)}
            disabled={!canManage}
          >
            <option value="single">Sola andata</option>
            <option value="double">Andata e ritorno</option>
          </select>
        </label>

        <div className="bg-gray-100 p-2 rounded col-span-1 md:col-span-2">
          <div className="text-center mb-3">
            <div className="grid grid-cols-2 gap-2">
            {["groups", "knockout"].map((opt) => (
              <Button
                key={opt}
                type="button"
                onClick={() => {
                  setStageType(opt as StageType);
                  setRankingStartPosition(opt === "knockout" ? 1 : 0);
                }}
                variant={stageType === opt ? "primary" : "ghost"}
                className="w-full"
              >
                {opt === "groups" ? "Gironi" : "Eliminatorie"}
              </Button>
            ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {stageType === "groups" && (
              <>
                <div>
                  <label className="text-sm flex justify-between items-center">
                    E' una fase finale?
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => {
                          setRankingStartPosition(1);
                          setNumGroups(1);
                          setWildcards(0);
                        }}
                        variant={rankingStartPosition === 1 ? "primary" : "ghost"}
                      >
                        Si
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setRankingStartPosition(0);
                          setNumGroups(2);
                          handleWildcards(2, qualifiers);
                        }}
                        variant={rankingStartPosition === 0 ? "primary" : "ghost"}
                      >
                        No
                      </Button>
                    </div>
                  </label>
                  {rankingStartPosition > 0 ? (
                    <i className="text-xs block my-1 text-gray-600">
                      Si: il risultato di questa fase andrà a determinare la classifica finale del torneo
                    </i>
                  ) : (
                    <i className="text-xs block my-1 text-gray-600">
                      No: questa fase servirà a determinare i partecipanti alla fase successiva. 
                      Al termine di essa, sarà possibile generare una nuova fase eliminatoria con i vincitori dei gironi.
                    </i>
                  )}
                </div>
                
                {rankingStartPosition > 0 ? (
                 <>
                  <label className="text-sm">
                    Posizione inizio ranking
                    <input
                      type="number"
                      min={0}
                      className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                      value={rankingStartPosition}
                      onChange={(e) => setRankingStartPosition(Number(e.target.value))}
                      disabled={!canManage}
                    />
                    <i className="text-xs block my-1 text-gray-600">
                      Compila solo se il risultato di questa fase andrà a determinare la classifica finale del torneo
                    </i>
                  </label>
                 </>
                ) : (
                  <>
                    <label className="text-sm">
                      Numero gironi
                      <input
                        type="number"
                        min={1}
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                        value={numGroups}
                        onChange={(e) => {
                          setNumGroups(Number(e.target.value))
                          handleWildcards(Number(e.target.value), qualifiers)
                        }}
                        disabled={!canManage}
                      />
                    </label>
                    <label className="text-sm">
                      Assegnazione partecipanti
                      <select
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                        value={seedingMethod}
                        onChange={(e) => setSeedingMethod(e.target.value as SeedingMethod)}
                        disabled={!canManage}
                      >
                        <option value="weighted">A fasce</option>
                        <option value="random">Random</option>
                      </select>
                    </label>
                    <label className="text-sm">
                      Qualificati per girone
                      <input
                        type="number"
                        min={0}
                        className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                        value={qualifiers}
                        onChange={(e) => {
                          setQualifiers(Number(e.target.value))
                          handleWildcards(numGroups, Number(e.target.value))
                        }}
                        disabled={!canManage}
                      />
                    </label>
                        
                    <div>
                      <label className="text-sm">
                        Wildcards
                        <input
                          type="number"
                          min={0}
                          className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                          value={wildcards}
                          onChange={(e) => setWildcards(Number(e.target.value))}
                          disabled={true}
                        />
                      </label>
                      {erroreWildcards && (
                        <i className="text-xs md:col-span-2 text-red-500">
                          {erroreWildcards}
                        </i>
                      )}
                    </div>

                    <label className="text-sm md:col-span-2">
                      Criteri di spareggio
                      <div className="mt-1 w-full rounded px-2 py-1">
                      {tieBreakers.split(",").map((val, index) => (
                        <div
                          key={val}
                          className="flex items-center justify-between rounded mb-1 bg-gray-200 px-3 py-1"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 text-sm text-gray-500">
                              {index + 1}
                            </span>

                            <span className="font-medium">{TIE_BREAKER_OPTIONS.find((option) => option.value === val)?.label}</span>
                          </div>

                          <div className="flex gap-1">
                            <button
                              onClick={() => handleTieBreakersMoveUp(index)}
                              disabled={index === 0}
                              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-30"
                            >
                              ↑
                            </button>

                            <button
                              onClick={() => handleTieBreakersMoveDown(index)}
                              disabled={index === TIE_BREAKER_OPTIONS.length - 1}
                              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-30"
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      ))}
                      </div>
                    </label>
                  </>
                )}

              </>
            )}

            {stageType === "knockout" && (
              <>
                <label className="text-sm">
                  Partenza tabellone
                  <select
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                    value={knockoutLevel}
                    onChange={(e) => setKnockoutLevel(e.target.value as KnockoutLevel)}
                    disabled={!canManage}
                  >
                    {KNOCKOUT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  Posizione inizio ranking
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
                    value={rankingStartPosition}
                    onChange={(e) => setRankingStartPosition(Number(e.target.value))}
                    disabled={!canManage}
                  />
                  <i className="text-xs block my-1 text-gray-600">
                    Compila solo se il risultato di questa fase andrà a determinare la classifica finale del torneo
                  </i>
                </label>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm text-gray-600">Partecipanti disponibili: {participantsCount}</span>
        {canManage ? (
          <Button onClick={handleCreate} disabled={creating || participantsCount < 2 || erroreWildcards.length > 0}>
            {creating ? "Creazione..." : "Crea stage"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
